import Phaser from 'phaser';
import { GRID_SIZE, SIDEBAR, WORKSPACE, isInsideGrid, worldToGrid } from '../grid';
import { Organ, type OrganDef } from '../entities/Organ';

export interface OrganPoolCallbacks {
  /** Called when the student drops an organ onto a valid workspace cell. */
  onPlace: (def: OrganDef, cell: { col: number; row: number }) => boolean;
  /** Called when the student starts/finishes dragging — lets PipeBuilder pause. */
  onDragStateChange: (dragging: boolean) => void;
}

/**
 * Renders draggable organ "icons" (composite: organ body + cone protrusions) in the
 * right sidebar. The icon and the drag ghost both show the FULL organ so students see
 * the complete shape they're about to place.
 */
export class OrganPool {
  private scene: Phaser.Scene;
  private cb: OrganPoolCallbacks;
  private icons: Phaser.GameObjects.Container[] = [];
  /**
   * Maps the interactive hit-zone game object → its def. Drag/dragend events
   * fire on the zone, not the visual icon. We key off the zone because Phaser
   * Container hit-areas behave badly under `setScale` (the rect's width/height
   * get scaled but the x/y offsets do not, so a Rect(-h, -h, 2h, 2h) on a
   * scale-0.5 container ends up covering only the upper-left quadrant). Using
   * an unscaled Zone for hit detection sidesteps the quirk entirely.
   */
  private iconDefs = new Map<Phaser.GameObjects.Zone, OrganDef>();
  /** Maps zone → its visual icon, so dragstart can hide the icon. */
  private zoneIcons = new Map<Phaser.GameObjects.Zone, Phaser.GameObjects.Container>();
  private zones: Phaser.GameObjects.Zone[] = [];
  private ghost: Phaser.GameObjects.Container | null = null;

  // The legend now lives in its OWN panel above the sidebar (see grid.ts
  // `LEGEND`), so the pool starts near the top of the sidebar with just a
  // small internal pad. POOL_ITEM_H is tight so all six organs fit on the
  // capstone level — the tallest icon (heart with N + S cones, ~144px scaled)
  // is allowed to visually nudge into the next row's cone-margin space, which
  // reads fine since cones extend OUTSIDE the body silhouette.
  private static readonly POOL_TOP = SIDEBAR.y + 20;
  private static readonly POOL_ITEM_H = 130;
  private static readonly ICON_SCALE = 0.5;

  constructor(scene: Phaser.Scene, cb: OrganPoolCallbacks) {
    this.scene = scene;
    this.cb = cb;
    this.scene.input.on('drag', this.handleDrag, this);
    this.scene.input.on('dragend', this.handleDragEnd, this);
  }

  /** Replace the pool contents with the organs from the current level. */
  setOrgans(organs: OrganDef[]): void {
    this.clear();
    organs.forEach((def, idx) => this.addIcon(def, idx));
  }

  /** Remove an icon for an organ that's been placed in the workspace. */
  removeIcon(defId: string): void {
    for (let i = 0; i < this.zones.length; i++) {
      const zone = this.zones[i];
      const def = this.iconDefs.get(zone);
      if (def && def.id === defId) {
        const icon = this.zoneIcons.get(zone);
        icon?.destroy();
        zone.destroy();
        if (icon) {
          const iconIdx = this.icons.indexOf(icon);
          if (iconIdx >= 0) this.icons.splice(iconIdx, 1);
        }
        this.iconDefs.delete(zone);
        this.zoneIcons.delete(zone);
        this.zones.splice(i, 1);
        return;
      }
    }
  }

  /** Add an organ icon back to the pool (after a level reset). */
  restoreIcon(def: OrganDef): void {
    const idx = this.icons.length;
    this.addIcon(def, idx);
  }

  clear(): void {
    for (const icon of this.icons) icon.destroy();
    for (const zone of this.zones) zone.destroy();
    this.icons = [];
    this.zones = [];
    this.iconDefs.clear();
    this.zoneIcons.clear();
    if (this.ghost) {
      this.ghost.destroy();
      this.ghost = null;
    }
  }

  private addIcon(def: OrganDef, idx: number): void {
    const cx = SIDEBAR.x + SIDEBAR.width / 2;
    const cy = OrganPool.POOL_TOP + idx * OrganPool.POOL_ITEM_H + OrganPool.POOL_ITEM_H / 2;
    const icon = Organ.buildPreview(this.scene, def, { scale: OrganPool.ICON_SCALE });
    icon.setPosition(cx, cy);
    icon.setDepth(15);

    // Hit area lives on a separate Zone game object — see iconDefs JSDoc for
    // the Container-scale quirk this works around. Size is computed in WORLD
    // coords (visible body + cone protrusions + padding) so the entire icon
    // plus a finger-friendly margin is grabbable.
    //   body:  def.cols/rows × GRID_SIZE
    //   cones: 2 × CONE_LEN (88px) on each axis where ports protrude
    //   pad:   extra finger margin
    // All multiplied by ICON_SCALE since those are local-coord lengths.
    const pad = 40;
    const zoneW = (def.cols * GRID_SIZE + 88 + pad) * OrganPool.ICON_SCALE;
    const zoneH = (def.rows * GRID_SIZE + 88 + pad) * OrganPool.ICON_SCALE;
    const zone = this.scene.add
      .zone(cx, cy, zoneW, zoneH)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true, draggable: true });
    // Sits above the icon so it intercepts pointer events without affecting visuals.
    zone.setDepth(16);

    this.iconDefs.set(zone, def);
    this.zoneIcons.set(zone, icon);
    this.icons.push(icon);
    this.zones.push(zone);

    zone.on('dragstart', () => this.handleDragStart(zone, icon, def));
  }

  private handleDragStart(
    zone: Phaser.GameObjects.Zone,
    icon: Phaser.GameObjects.Container,
    def: OrganDef,
  ): void {
    this.cb.onDragStateChange(true);
    if (this.ghost) this.ghost.destroy();
    this.ghost = Organ.buildPreview(this.scene, def, { alpha: 0.7 });
    this.ghost.setPosition(zone.x, zone.y);
    this.ghost.setDepth(80);
    icon.setVisible(false);
  }

  private handleDrag(
    _pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
    dragX: number,
    dragY: number,
  ): void {
    // Scene-level `drag` fires for EVERY draggable (placed organs included).
    // Only follow the pointer when the dragged object is one of our pool zones.
    if (!this.iconDefs.has(gameObject as Phaser.GameObjects.Zone)) return;
    if (this.ghost) this.ghost.setPosition(dragX, dragY);
  }

  private handleDragEnd(
    pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
  ): void {
    const zone = gameObject as Phaser.GameObjects.Zone;
    const def = this.iconDefs.get(zone);
    // Not our drag — a placed organ probably. Don't touch pool state or
    // PipeBuilder enablement; GameScene's per-sprite dragend handler owns that.
    if (!def) return;
    const icon = this.zoneIcons.get(zone);
    if (!this.ghost) {
      this.cb.onDragStateChange(false);
      return;
    }
    const center = worldToGrid(pointer.worldX, pointer.worldY);
    const cell = {
      col: Math.max(0, Math.min(WORKSPACE.cols - def.cols, center.col - Math.floor(def.cols / 2))),
      row: Math.max(0, Math.min(WORKSPACE.rows - def.rows, center.row - Math.floor(def.rows / 2))),
    };
    const inside = isInsideGrid(center) && this.organFitsInWorkspace(cell, def);
    if (!inside) {
      this.cleanupGhost();
      icon?.setVisible(true);
      this.cb.onDragStateChange(false);
      return;
    }
    const placed = this.cb.onPlace(def, cell);
    if (placed) {
      this.removeIcon(def.id);
    } else {
      icon?.setVisible(true);
    }
    this.cleanupGhost();
    this.cb.onDragStateChange(false);
  }

  private cleanupGhost(): void {
    if (this.ghost) {
      this.ghost.destroy();
      this.ghost = null;
    }
  }

  private organFitsInWorkspace(cell: { col: number; row: number }, def: OrganDef): boolean {
    return (
      cell.col >= 0 &&
      cell.row >= 0 &&
      cell.col + def.cols <= WORKSPACE.cols &&
      cell.row + def.rows <= WORKSPACE.rows
    );
  }
}
